import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { FutureContractTypeEnum } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { z } from "zod";

const formSchema = z.object({
	name: z.string().min(1),
	type: z.nativeEnum(FutureContractTypeEnum),
	monthly: z.number().min(0),
	lumpSum: z.number().min(0),
	loanAmount: z.number().min(0),
	startDate: z.date().optional(),
	note: z.string().optional(),
});

export type PinFutureContractFormValues = z.infer<typeof formSchema>;

const getDefaultStartDate = () => {
	const now = new Date();
	return new Date(now.getFullYear() + 1, now.getMonth(), 1);
};

export const usePinFutureContractForm = () => {
	const { t } = useResource();

	const form = useForm<PinFutureContractFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: "",
			type: FutureContractTypeEnum.Savings,
			monthly: 0,
			lumpSum: 0,
			loanAmount: 0,
			startDate: getDefaultStartDate(),
			note: "",
		},
	});

	const { watch, formState } = form;

	const type = watch("type");
	const monthly = watch("monthly");
	const lumpSum = watch("lumpSum");
	const loanAmount = watch("loanAmount");
	const name = watch("name");

	const isLoanType =
		type === FutureContractTypeEnum.Mortgage ||
		type === FutureContractTypeEnum.Loan;

	const canSubmit =
		name.trim().length > 0 &&
		(isLoanType ? loanAmount > 0 : monthly > 0 || lumpSum > 0) &&
		!formState.isSubmitting;

	const typeOptions = useMemo(
		() => [
			{
				value: FutureContractTypeEnum.Savings,
				label: t(Resources.Goals.Detail.Solution.Pin.Future.Types.savings),
			},
			{
				value: FutureContractTypeEnum.Mortgage,
				label: t(Resources.Goals.Detail.Solution.Pin.Future.Types.mortgage),
			},
			{
				value: FutureContractTypeEnum.Loan,
				label: t(Resources.Goals.Detail.Solution.Pin.Future.Types.loan),
			},
			{
				value: FutureContractTypeEnum.Insurance,
				label: t(Resources.Goals.Detail.Solution.Pin.Future.Types.insurance),
			},
			{
				value: FutureContractTypeEnum.Pension,
				label: t(Resources.Goals.Detail.Solution.Pin.Future.Types.pension),
			},
		],
		[t],
	);

	return {
		form,
		typeOptions,
		isLoanType,
		canSubmit,
	};
};
